import type { MeResponse, SessionUser } from '@emailforge/schemas';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

type AuthState = {
  user: SessionUser | null;
  activeOrganizationId: string | null;
  memberships: MeResponse['memberships'];
  featureFlags: Record<string, boolean>;
  accessToken: string | null;
  refreshToken: string | null;
  isInitialized: boolean;
};

const ACCESS_TOKEN_KEY = 'emailforge.accessToken';
const REFRESH_TOKEN_KEY = 'emailforge.refreshToken';

function readStored(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string | null) {
  if (typeof window === 'undefined') return;
  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch {
    // ignore storage failures (private mode, quota)
  }
}

const initialState: AuthState = {
  user: null,
  activeOrganizationId: null,
  memberships: [],
  featureFlags: {},
  accessToken: readStored(ACCESS_TOKEN_KEY),
  refreshToken: readStored(REFRESH_TOKEN_KEY),
  isInitialized: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setSession(
      state,
      action: PayloadAction<{
        me: MeResponse;
        accessToken: string;
        refreshToken?: string;
      }>,
    ) {
      state.user = action.payload.me.user;
      state.memberships = action.payload.me.memberships;
      state.activeOrganizationId = action.payload.me.activeOrganizationId;
      state.featureFlags = action.payload.me.featureFlags;
      state.accessToken = action.payload.accessToken;
      state.isInitialized = true;
      writeStored(ACCESS_TOKEN_KEY, action.payload.accessToken);
      if (action.payload.refreshToken !== undefined) {
        state.refreshToken = action.payload.refreshToken;
        writeStored(REFRESH_TOKEN_KEY, action.payload.refreshToken);
      }
    },
    /**
     * Used by the apiSlice's reauth interceptor when only the access token
     * has rotated (e.g. after a successful POST /auth/refresh). Doesn't touch
     * user/memberships — those persist across refreshes.
     */
    rotatedTokens(
      state,
      action: PayloadAction<{ accessToken: string; refreshToken: string }>,
    ) {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      writeStored(ACCESS_TOKEN_KEY, action.payload.accessToken);
      writeStored(REFRESH_TOKEN_KEY, action.payload.refreshToken);
    },
    setActiveOrganization(state, action: PayloadAction<string>) {
      state.activeOrganizationId = action.payload;
    },
    signedOut(state) {
      state.user = null;
      state.memberships = [];
      state.activeOrganizationId = null;
      state.featureFlags = {};
      state.accessToken = null;
      state.refreshToken = null;
      state.isInitialized = true;
      writeStored(ACCESS_TOKEN_KEY, null);
      writeStored(REFRESH_TOKEN_KEY, null);
    },
    markInitialized(state) {
      state.isInitialized = true;
    },
  },
});

export const {
  setSession,
  rotatedTokens,
  setActiveOrganization,
  signedOut,
  markInitialized,
} = authSlice.actions;
export default authSlice.reducer;
